import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { articles } from "@/data/articles";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Journal() {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/subscribe`, { email, source: "newsletter" });
      setDone(true);
      toast.success("You're subscribed — welcome to the Journal.");
      setEmail("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="journal-page">
      {/* HERO */}
      <section className="pt-40 md:pt-52 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-terracotta mb-8">The Journal</p>
            <h1 className="font-serif font-light text-wine text-4xl sm:text-5xl md:text-6xl leading-[1.1] tracking-tight max-w-3xl">
              Field notes on leading a career, a family, and yourself.
            </h1>
          </Reveal>
        </div>
      </section>

      {/* ARTICLES */}
      <section className="pb-24 md:pb-32" data-testid="journal-list">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="border-t border-gold/40">
            {articles.map((a, i) => (
              <Reveal key={a.slug} delay={i * 0.06}>
                <Link
                  to={`/journal/${a.slug}`}
                  data-testid={`article-link-${a.slug}`}
                  className="group grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8 py-10 md:py-12 items-baseline border-b border-gold/40 hover:pl-3 transition-[padding] duration-300"
                >
                  <div className="md:col-span-3">
                    <p className="text-xs uppercase tracking-[0.2em] text-terracotta">{a.category}</p>
                    <p className="text-xs text-charcoal/50 mt-2">{a.date}</p>
                  </div>
                  <div className="md:col-span-7">
                    <h2 className="font-serif text-2xl md:text-3xl text-wine leading-snug group-hover:text-terracotta transition-colors">
                      {a.title}
                    </h2>
                    <p className="mt-3 text-charcoal/70 leading-relaxed">{a.excerpt}</p>
                  </div>
                  <div className="md:col-span-2 md:text-right">
                    <ArrowUpRight className="inline text-terracotta opacity-0 group-hover:opacity-100 transition-opacity" size={24} />
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="bg-charcoal text-cream py-24 md:py-32" data-testid="newsletter-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6">The Newsletter</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl leading-tight">
              A little clarity, delivered.
            </h2>
            <p className="mt-6 text-cream/75 leading-relaxed max-w-xl mx-auto">
              Occasional notes on systems, leadership, and sustainable success — written for the ambitious
              mother who refuses to choose. No noise, no guilt.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            {done ? (
              <p className="mt-10 font-serif italic text-2xl text-blush" data-testid="newsletter-success">
                Thank you — you're on the list.
              </p>
            ) : (
              <form onSubmit={subscribe} className="mt-10 flex flex-col sm:flex-row gap-4 max-w-lg mx-auto" data-testid="newsletter-form">
                <input
                  required
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email address"
                  data-testid="newsletter-email-input"
                  className="flex-1 bg-transparent border-b-2 border-cream/30 py-3 text-cream placeholder:text-cream/40 focus:outline-none focus:border-blush transition-colors text-center sm:text-left"
                />
                <motion.button
                  whileTap={{ scale: 0.97 }}
                  disabled={sending}
                  type="submit"
                  data-testid="newsletter-submit-button"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-terracotta text-cream px-8 py-3.5 text-sm font-medium tracking-wide hover:bg-blush hover:text-charcoal transition-colors duration-300 disabled:opacity-60"
                >
                  {sending ? "Joining…" : "Subscribe"}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </motion.button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
}
